import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { BuildingGroup } from "../../../shared/Models/read/BuildingGroup";
import { AddBuildingRangeToBuildingGroupDto } from "../../../shared/Models/write/AddBuildingRangeToBuildingGroupDto";
import { ToastService } from "../../../shared/toast.service";
import { BuildingGroupService } from "../../building-list/services/building-group.service";
import { BuildingSurveyViewModel } from "src/app/shared/Models/buildingTableViewModel";
import { SurveyStatus } from "src/app/shared/Models/enums/SurveySurveyStatus";
import { BuildingTypeEnum } from "src/app/shared/Models/enums/BuildingTypeEnum";
import { Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import { Area } from "../../../shared/Models/read/Area";
import { SessionService } from "../../survey/shared/services/session.service";
import { TsaRemovalService } from "src/app/shared/tsa-removal.service";
import {
  DialogService,
  DialogType,
} from "src/app/shared/services/dialog.service";

@Component({
  selector: "app-building-group-add-building-to-group",
  templateUrl: "./building-group-add-building-to-group.component.html",
  styleUrls: ["./building-group-add-building-to-group.component.scss"],
})
export class BuildingGroupAddBuildingToGroupComponent
  implements OnInit, OnDestroy
{
  @Input() areaId: string;
  @Input() buildings: Array<BuildingSurveyViewModel>;
  @Output() passEntry: EventEmitter<any> = new EventEmitter();
  @Output() reloadBuildingList: EventEmitter<any> = new EventEmitter();

  buildingGroups: BuildingGroup[];
  selectedBuildingGroupId: string;
  buildingGroupName: string;

  multipleInsideSurveysAvailble: boolean;
  buildingsWithInsideSurvey: Array<BuildingSurveyViewModel>;
  selectedBuildingId: string = null;

  currentArea: Area;

  constructor(
    public activeModal: NgbActiveModal,
    private buildingGroupService: BuildingGroupService,
    private toastService: ToastService,
    private sessionService: SessionService,
    private tsaRemovalService: TsaRemovalService,
    private dialogService: DialogService
  ) {}
  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
  ngOnInit() {
    this.buildingGroupService
      .getBuildingGroupsByAreaId(this.areaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: BuildingGroup[]) => {
        this.buildingGroups = res;
      });
  }

  openAddToNewBuildingGroupModal() {
    this.activeModal.close("open-add-to-new-modal");
  }

  onSelectedGroup(id) {
    this.selectedBuildingGroupId = id;
  }

  onSelectedBuilding(id) {
    this.selectedBuildingId = id;
  }

  async addBuildingsToGroup() {
    if (!this.selectedBuildingGroupId) {
      this.toastService.showWarnToast(
        $localize`:@@validation.buildinggroup.nonselected:No building group selected`,
        $localize`:@@validation.buildinggroup.selectbuildinggroup:Please select a building group.`
      );
      return;
    }

    let allBuildings = [...this.buildings];
    let currentBuildingGroupBuildings;

    const currentBuildingGroup = this.buildingGroups.find(
      (buildingGroup) => buildingGroup.id == this.selectedBuildingGroupId
    );
    if (currentBuildingGroup) {
      currentBuildingGroupBuildings = await this.buildingGroupService
        .getBuildingsByBuildingGroupId(currentBuildingGroup.id)
        .toPromise();

      allBuildings = [...this.buildings, ...currentBuildingGroupBuildings];
    }

    const error = this.getValidationError(allBuildings);
    if (error) return;

    this.buildingsWithInsideSurvey =
      this.getBuildingsWithInsideSurvey(allBuildings);

    const surveyId = this.getSelectedSurvey();

    if (surveyId === undefined) return;

    const addBuildingRangeToBuildingGroupDto =
      new AddBuildingRangeToBuildingGroupDto({
        buildingIds: this.buildings.map((building) => building.buildingId),
        buildingGroupId: this.selectedBuildingGroupId,
        surveyId: surveyId,
      });

    var detailText =
      this.tsaRemovalService.getRemarkForBuildingSurveyViewModels(
        allBuildings,
        ""
      );

    if (detailText)
      this.dialogService
        .openDialog({
          headerText: $localize`:@@buildinggroup.addbuilding.addselectedbuildingtitle:Add selected buildings to existing building group`,
          detailText: detailText,
          dialogType: DialogType.Continue,
          fullscreen: true,
          zIndex: 99999,
        })
        .pipe(first())
        .subscribe((feedback) => {
          if (feedback)
            this.handleAddingBuildingToGroup(
              addBuildingRangeToBuildingGroupDto
            );
        });
    else this.handleAddingBuildingToGroup(addBuildingRangeToBuildingGroupDto);
  }

  private handleAddingBuildingToGroup(
    addBuildingRangeToBuildingGroupDto: AddBuildingRangeToBuildingGroupDto
  ) {
    this.buildingGroupService
      .addBuildingsToExistingGroup(addBuildingRangeToBuildingGroupDto)
      .pipe(first())
      .subscribe(
        (res) => {
          this.passEntry.emit(this.buildings);
          this.activeModal.dismiss();
          this.reloadBuildingList.emit();
        },
        (err) => {
          this.toastService.showErrorToast(
            $localize`:@@general.error:Something went wrong`,
            $localize`:@@bg.buildinggroupadd.error:Something went wrong while adding the building to the building group.`
          );
        }
      );
  }

  private getSelectedSurvey(): string {
    let surveyId;
    if (
      !this.buildingsWithInsideSurvey ||
      this.buildingsWithInsideSurvey.length === 0
    )
      surveyId = null;
    if (this.buildingsWithInsideSurvey.length === 1)
      surveyId = this.buildingsWithInsideSurvey[0].insideSurveyId;
    if (this.buildingsWithInsideSurvey.length > 1)
      surveyId = this.selectSurveyId();

    return surveyId;
  }

  private selectSurveyId(): string {
    if (this.selectedBuildingId === null) {
      this.multipleInsideSurveysAvailble = true;
      return undefined;
    }
    return this.buildingsWithInsideSurvey.find(
      (b) => b.buildingId === this.selectedBuildingId
    ).insideSurveyId;
  }

  getBuildingsWithInsideSurvey(
    buildingGroupBuildings: BuildingSurveyViewModel[]
  ): BuildingSurveyViewModel[] {
    return buildingGroupBuildings.filter((b) => b.insideSurveyId);
  }

  CreateBuildingGroupName(mainBuilding: BuildingCompareType): string {
    var buildingNameArray = mainBuilding.street.split("/");
    var streetName =
      buildingNameArray[0] +
      (buildingNameArray.length > 1
        ? "-" + buildingNameArray[buildingNameArray.length - 1]
        : "");

    this.currentArea = this.sessionService.getBlock();

    this.buildingGroupName = `${this.currentArea.areaName}.${streetName}.${mainBuilding.number}.${mainBuilding.suffix}`;
    return this.buildingGroupName;
  }

  private getValidationError(buildings: BuildingSurveyViewModel[]): boolean {
    const emptyOrNotInUseBuildings = this.buildings.some((building) =>
      [BuildingTypeEnum.EmptyParcel, BuildingTypeEnum.NotInUse].includes(
        building.buildingType
      )
    );

    if (emptyOrNotInUseBuildings) {
      this.toastService.showWarnToast(
        $localize`:@@validation.buildinggroup.add.invalidtype:Invalid buildingType`,
        $localize`:@@validation.buildinggroup.add.invalidtype.notinuse:Buildings of type EmptyParcel or NotInUSe are not allowed in a building group`
      );
      return true;
    }

    const anyNoAccessBuildings = buildings.some(
      (building) => building.buildingType === BuildingTypeEnum.NoAccess
    );
    const allNoAccesBuildings = buildings.every(
      (building) => building.buildingType === BuildingTypeEnum.NoAccess
    );

    if (anyNoAccessBuildings && !allNoAccesBuildings) {
      this.toastService.showWarnToast(
        $localize`:@@validation.buildinggroup.add.invalidtype:Invalid buildingType`,
        $localize`:@@validation.buildinggroup.add.invalidtype.noaccess:Can only add NoAccess building if all buildings are of type NoAccess`
      );
      return true;
    }

    return false;
  }

  private arrayMaxUnits(buildings: Array<BuildingCompareType>) {
    let max = -Infinity;
    for (let i = 0; i < buildings.length; i++)
      if (buildings[i].unitCount > max) max = buildings[i].unitCount;

    return max;
  }

  private comparers(a, b) {
    if (a > b) return +1;

    if (a < b) return -1;

    return 0;
  }

  private MapBuildingSurveyViewModelToBuildingCompareType(
    building: BuildingSurveyViewModel
  ): BuildingCompareType {
    let buildingcomparer = new BuildingCompareType();
    buildingcomparer.number = building.number.toString();
    buildingcomparer.suffix = building.suffix;
    buildingcomparer.id = building.buildingId;
    buildingcomparer.street = building.street;
    buildingcomparer.unitCount = building.totalNumberOfUnits;
    buildingcomparer.surveyStatus = building.surveyStatus;
    return buildingcomparer;
  }
}

class BuildingCompareType {
  id: string;
  street: string;
  number: string;
  suffix: string;
  unitCount: number;
  surveyStatus: SurveyStatus;
}
